import React, { useEffect, useState } from 'react';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BarChart3, Download, Grid3x3, ClipboardList, Users, UserCircle, Pencil, ShieldCheck, AlertTriangle, ShieldAlert } from 'lucide-react';
import { CriticalSkillsTab } from './CriticalSkillsTab';

const dl = (res, filename) => {
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

export default function HRReportsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [cellEdit, setCellEdit] = useState(null);
  const [savingCell, setSavingCell] = useState(false);
  const [groups, setGroups] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [skills, setSkills] = useState([]);
  const [emps, setEmps] = useState([]);
  // skill matrix filters
  const [mGroup, setMGroup] = useState('all');
  const [mFunc, setMFunc] = useState('all');
  const [matrix, setMatrix] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  // plan vs actual
  const [pva, setPva] = useState(null);
  // availability
  const [availSkill, setAvailSkill] = useState('');
  const [availLevel, setAvailLevel] = useState('S1');
  const [avail, setAvail] = useState(null);
  // employee report
  const [repEmp, setRepEmp] = useState('');
  const [empRep, setEmpRep] = useState(null);
  // position eligibility
  const [fpList, setFpList] = useState([]);
  const [peFp, setPeFp] = useState('');
  const [pe, setPe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [g, f, s, e, fp] = await Promise.all([
          hrAPI.listMaster('skill-groups'), hrAPI.listMaster('functions'), hrAPI.listSkills(), hrAPI.listEmployees(),
          hrAPI.listFunctionalPositions(),
        ]);
        setGroups(g.data || []); setFunctions(f.data || []); setSkills(s.data || []); setEmps(e.data || []);
        setFpList(fp.data || []);
      } catch { toast.error('Failed to load report options'); }
    })();
  }, []);

  const runPe = async (fpId) => {
    setPeFp(fpId);
    if (!fpId) { setPe(null); return; }
    try { setPe((await hrAPI.positionEligibility(fpId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const downloadMatrix = async () => {
    try {
      const params = {};
      if (mGroup !== 'all') params.skill_group_id = mGroup;
      if (mFunc !== 'all') params.function_id = mFunc;
      dl(await hrAPI.exportSkillMatrix(params), 'Skill_Matrix_F-HR-02.xlsx');
    } catch { toast.error('Failed to download skill matrix'); }
  };

  const loadMatrix = async () => {
    setLoadingMatrix(true);
    try {
      const params = {};
      if (mGroup !== 'all') params.skill_group_id = mGroup;
      if (mFunc !== 'all') params.function_id = mFunc;
      setMatrix((await hrAPI.skillMatrixData(params)).data);
    } catch { toast.error('Failed to load skill matrix'); }
    finally { setLoadingMatrix(false); }
  };
  useEffect(() => { loadMatrix(); /* eslint-disable-next-line */ }, [mGroup, mFunc]);

  const cellClass = (c) => {
    if (!c) return 'bg-white text-slate-300';
    if (c.gap) return 'bg-red-100 text-red-700 font-semibold';
    if (c.meets) return 'bg-emerald-100 text-emerald-700 font-semibold';
    if (c.level) return 'bg-blue-50 text-blue-700';
    return 'bg-white text-slate-300';
  };

  const openCellEdit = (emp, skill, cell) => {
    if (!canManage) return;
    setCellEdit({ empId: emp.id, empName: emp.name, skillId: skill.id, skillName: skill.name, current: cell?.level || '', required: cell?.required || '' });
  };
  const saveCell = async (level) => {
    setSavingCell(true);
    try {
      await hrAPI.setSkillLevels(cellEdit.empId, [{ skill_id: cellEdit.skillId, level }]);
      toast.success(`${cellEdit.empName}: ${cellEdit.skillName} → ${level}`);
      setCellEdit(null); loadMatrix();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingCell(false); }
  };

  const loadPva = async () => {
    try { setPva((await hrAPI.planVsActual()).data); } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { loadPva(); }, []);
  const downloadPva = async () => {
    try { dl(await hrAPI.exportPlanVsActual(), 'Training_Plan_vs_Actual.xlsx'); } catch { toast.error('Failed'); }
  };

  const runAvail = async () => {
    if (!availSkill) { toast.error('Select a skill'); return; }
    try { setAvail((await hrAPI.skillAvailability({ skill_id: availSkill, min_level: availLevel })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const runEmpRep = async (id) => {
    setRepEmp(id);
    if (!id) { setEmpRep(null); return; }
    try { setEmpRep((await hrAPI.employeeReport(id)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const downloadEmpRep = async () => {
    if (!repEmp) return;
    try { dl(await hrAPI.exportEmployeeReport(repEmp), 'Skill_Profile.xlsx'); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="hr-reports-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><BarChart3 className="w-6 h-6 text-pink-600" /> HR Reports</h1>
        <p className="text-sm text-slate-500 mt-0.5">Skill matrix (F/HR/02), plan-vs-actual tracking, employee skill profiles & skill availability</p>
      </div>

      <Tabs defaultValue="matrix">
        <TabsList>
          <TabsTrigger value="matrix" data-testid="hr-report-tab-matrix"><Grid3x3 className="w-4 h-4 mr-1" /> Skill Matrix</TabsTrigger>
          <TabsTrigger value="pva" data-testid="hr-report-tab-pva"><ClipboardList className="w-4 h-4 mr-1" /> Plan vs Actual</TabsTrigger>
          <TabsTrigger value="avail" data-testid="hr-report-tab-avail"><Users className="w-4 h-4 mr-1" /> Skill Availability</TabsTrigger>
          <TabsTrigger value="emp" data-testid="hr-report-tab-emp"><UserCircle className="w-4 h-4 mr-1" /> Employee</TabsTrigger>
          <TabsTrigger value="pe" data-testid="hr-report-tab-pe"><ShieldCheck className="w-4 h-4 mr-1" /> Position Eligibility</TabsTrigger>
          <TabsTrigger value="crit" data-testid="hr-report-tab-crit"><ShieldAlert className="w-4 h-4 mr-1" /> Critical Skills</TabsTrigger>
        </TabsList>

        {/* Skill Matrix */}
        <TabsContent value="matrix">
          <Card className="border border-slate-200 shadow-none"><CardContent className="p-4 space-y-3">
            <p className="text-sm text-slate-600">Export the <b>F/HR/02 Skill Matrix</b> (Rev. {matrix?.revision || '02'}, Dt. {matrix?.rev_date || '12/10/2026'}) — employees × skills grouped by skill group, cells coloured by current level with required-vs-current gap highlighting. The footer rows show persons at S2+, the critical-skill flag and the backup person / plan.</p>
            <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
              <div><Label className="text-xs">Skill Group</Label>
                <Select value={mGroup} onValueChange={setMGroup}>
                  <SelectTrigger data-testid="hr-matrix-group"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All groups</SelectItem>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Function</Label>
                <Select value={mFunc} onValueChange={setMFunc}>
                  <SelectTrigger data-testid="hr-matrix-function"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All functions</SelectItem>{functions.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={downloadMatrix} data-testid="hr-matrix-download"><Download className="w-4 h-4" /> Download F/HR/02 (Excel)</Button>

            <div className="flex flex-wrap items-center gap-4 pt-1 text-[11px] text-slate-500">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-100 border border-red-300" /> Below required (gap)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300" /> Meets / exceeds</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-50 border border-blue-200" /> Has level (no requirement)</span>
              <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-500" /> Under-qualified for an assigned position</span>
              {matrix && <span className="ml-auto font-medium text-slate-600">{matrix.employee_count} employees · {matrix.skill_count} skills · <span className="text-red-600">{matrix.gap_total} gaps</span>{matrix.under_qualified_total ? <> · <span className="text-amber-600">{matrix.under_qualified_total} under-qualified</span></> : null}</span>}
            </div>

            {loadingMatrix ? <p className="text-sm text-slate-400 py-6 text-center">Loading matrix…</p> :
              !matrix || matrix.employees.length === 0 || matrix.skills.length === 0 ?
                <p className="text-sm text-slate-400 py-6 text-center">No matrix data for this filter</p> : (
                  <div className="border rounded-md overflow-auto max-h-[65vh]" data-testid="hr-matrix-grid">
                    <table className="border-collapse text-[11px]">
                      <thead>
                        <tr>
                          <th className="sticky left-0 top-0 z-30 bg-slate-100 border border-slate-200 px-2 py-1 text-left min-w-[180px]">Employee</th>
                          {matrix.groups.map(g => (
                            <th key={g.id} colSpan={g.skill_ids.length} className="sticky top-0 z-20 bg-slate-100 border border-slate-200 px-2 py-1 text-center font-semibold text-slate-700">{g.name}</th>
                          ))}
                        </tr>
                        <tr>
                          <th className="sticky left-0 top-[26px] z-30 bg-slate-50 border border-slate-200 px-2 py-1" />
                          {matrix.groups.flatMap(g => g.skill_ids.map(sid => {
                            const s = matrix.skills.find(x => x.id === sid);
                            return <th key={sid} className="sticky top-[26px] z-20 bg-slate-50 border border-slate-200 px-1 py-2 align-bottom whitespace-nowrap" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', minWidth: '26px', height: '110px' }}>{s?.name}</th>;
                          }))}
                        </tr>
                      </thead>
                      <tbody>
                        {matrix.employees.map(e => (
                          <tr key={e.id} data-testid={`hr-matrix-emp-${e.id}`}>
                            <td className="sticky left-0 z-10 bg-white border border-slate-200 px-2 py-1 font-medium text-slate-700 whitespace-nowrap" data-testid={`hr-matrix-emp-name-${e.id}`}>
                              <span className="inline-flex items-center gap-1">
                                {e.under_qualified && <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" title={`Under-qualified for: ${(e.ineligible_positions || []).join(', ')}`} />}
                                {e.name}{e.emp_code ? <span className="text-slate-400 font-normal"> · {e.emp_code}</span> : ''}
                              </span>
                            </td>
                            {matrix.groups.flatMap(g => g.skill_ids.map(sid => {
                              const c = e.cells[sid];
                              const s = matrix.skills.find(x => x.id === sid);
                              const title = c?.required ? `Required ${c.required}${c.level ? `, current ${c.level}` : ', not attained'}` : (c?.level ? `Level ${c.level}` : 'No level');
                              return <td key={sid} title={canManage ? title + ' — click to edit' : title} onClick={() => openCellEdit(e, s, c)} className={`border border-slate-200 text-center px-1 py-1 ${cellClass(c)} ${canManage ? 'cursor-pointer hover:ring-1 hover:ring-pink-300' : ''}`} data-testid={`hr-matrix-cell-${e.id}-${sid}`}>{c?.level || '·'}</td>;
                            }))}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {[
                          { key: 's2', label: 'No. of persons at S2 or above', get: st => (st?.s2_plus_count ?? 0), cls: () => 'bg-slate-50 text-slate-700 font-semibold' },
                          { key: 'crit', label: 'Critical skill (Y/N)', get: st => (st?.critical || 'N'), cls: st => st?.critical === 'Y' ? 'bg-red-100 text-red-700 font-bold' : 'bg-slate-50 text-slate-500' },
                          { key: 'backup', label: 'Backup person / plan (6-monthly review)', get: st => (st?.backup_text || '—'), cls: st => st?.critical === 'Y' && ['not_reviewed', 'overdue'].includes(st?.review_status) ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-600' },
                        ].map(row => (
                          <tr key={row.key} data-testid={`hr-matrix-footer-${row.key}`}>
                            <td className="sticky left-0 z-10 bg-slate-100 border border-slate-200 px-2 py-1 font-semibold text-slate-700 text-[10px] leading-tight">{row.label}</td>
                            {matrix.groups.flatMap(g => g.skill_ids.map(sid => {
                              const st = matrix.skill_stats?.[sid];
                              return (
                                <td key={sid} title={row.key === 'crit' ? (st?.critical_reason || 'Not critical') : undefined}
                                  className={`border border-slate-200 text-center px-1 py-1 text-[10px] ${row.cls(st)} ${row.key === 'backup' ? 'min-w-[120px] whitespace-normal' : ''}`}
                                  data-testid={`hr-matrix-footer-${row.key}-${sid}`}>{row.get(st)}</td>
                              );
                            }))}
                          </tr>
                        ))}
                      </tfoot>
                    </table>
                  </div>
                )}
          </CardContent></Card>
        </TabsContent>

        {/* Plan vs Actual */}
        <TabsContent value="pva">          <Card className="border border-slate-200 shadow-none"><CardContent className="p-4 space-y-3">
            {pva && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="hr-pva-summary">
                {[['Total', pva.summary.total, 'bg-white border-slate-200 text-slate-700'], ['Completed', pva.summary.completed, 'bg-emerald-50 border-emerald-200 text-emerald-700'], ['Pending', pva.summary.pending, 'bg-amber-50 border-amber-200 text-amber-700'], ['Overdue', pva.summary.overdue, 'bg-red-50 border-red-200 text-red-700'], ['Completion', `${pva.summary.completion_pct}%`, 'bg-pink-50 border-pink-200 text-pink-700']].map(([l, v, c]) => (
                  <div key={l} className={`rounded-lg border px-3 py-2 ${c}`}><p className="text-lg font-bold leading-none">{v}</p><p className="text-[10px] uppercase tracking-wide mt-1">{l}</p></div>
                ))}
              </div>
            )}
            <Button variant="outline" className="gap-1" onClick={downloadPva} data-testid="hr-pva-download"><Download className="w-4 h-4" /> Export Excel</Button>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs">Employee</TableHead><TableHead className="text-xs">Skill</TableHead>
                  <TableHead className="text-xs">Req.</TableHead><TableHead className="text-xs">Current</TableHead>
                  <TableHead className="text-xs">Planned</TableHead><TableHead className="text-xs">Actual</TableHead><TableHead className="text-xs">Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(pva?.rows || []).length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6 text-sm">No plan data</TableCell></TableRow> :
                    pva.rows.map((r, i) => (
                      <TableRow key={i} className={r.status === 'overdue' ? 'bg-red-50/40' : ''}>
                        <TableCell className="text-sm">{r.employee}</TableCell><TableCell className="text-sm text-slate-600">{r.skill}</TableCell>
                        <TableCell className="text-xs font-mono">{r.required_level || '—'}</TableCell><TableCell className="text-xs font-mono">{r.current_level || '—'}</TableCell>
                        <TableCell className="text-xs font-mono">{r.planned_date || '—'}</TableCell><TableCell className="text-xs font-mono">{r.actual_date || '—'}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] ${r.status === 'overdue' ? 'bg-red-50 text-red-700 border-red-200' : r.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{r.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* Availability */}
        <TabsContent value="avail">
          <Card className="border border-slate-200 shadow-none"><CardContent className="p-4 space-y-3">
            <p className="text-sm text-slate-600">Find employees who can perform a skill at a minimum level (who can do skill X at ≥ level Y).</p>
            <div className="grid sm:grid-cols-3 gap-3 max-w-2xl items-end">
              <div><Label className="text-xs">Skill</Label>
                <Select value={availSkill || undefined} onValueChange={setAvailSkill}>
                  <SelectTrigger data-testid="hr-avail-skill"><SelectValue placeholder="Select skill" /></SelectTrigger>
                  <SelectContent>{skills.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Min Level</Label>
                <Select value={availLevel} onValueChange={setAvailLevel}>
                  <SelectTrigger data-testid="hr-avail-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{['S1', 'S2', 'S3', 'S4'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button className="bg-pink-600 hover:bg-pink-700 text-white" onClick={runAvail} data-testid="hr-avail-run">Find</Button>
            </div>
            {avail && (
              <div className="border rounded-md overflow-hidden mt-2" data-testid="hr-avail-results">
                <div className="px-3 py-2 bg-slate-50 text-xs text-slate-600">{avail.count} employee(s) at ≥ {avail.min_level}</div>
                <Table>
                  <TableHeader><TableRow><TableHead className="text-xs">Employee</TableHead><TableHead className="text-xs">Emp Code</TableHead><TableHead className="text-xs">Level</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {avail.employees.length === 0 ? <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-6 text-sm">No one qualifies yet</TableCell></TableRow> :
                      avail.employees.map(e => <TableRow key={e.employee_id}><TableCell className="text-sm">{e.name}</TableCell><TableCell className="text-xs font-mono">{e.emp_code || '—'}</TableCell><TableCell><Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{e.level}</Badge></TableCell></TableRow>)}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Employee report */}
        <TabsContent value="emp">
          <Card className="border border-slate-200 shadow-none"><CardContent className="p-4 space-y-3">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="w-72"><Label className="text-xs">Employee</Label>
                <Select value={repEmp || undefined} onValueChange={runEmpRep}>
                  <SelectTrigger data-testid="hr-emprep-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {empRep && <Button variant="outline" className="gap-1" onClick={downloadEmpRep} data-testid="hr-emprep-download"><Download className="w-4 h-4" /> Export Excel</Button>}
            </div>
            {empRep && (
              <div className="space-y-4" data-testid="hr-emprep-body">
                <p className="text-sm"><b>{empRep.employee.name}</b> · {empRep.employee.emp_code || 'no code'} · {empRep.employee.experience_years || 0} yr exp</p>
                <div>
                  <p className="text-sm font-semibold mb-1">Skill Levels ({empRep.skill_levels.length})</p>
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50/80"><TableHead className="text-xs">Group</TableHead><TableHead className="text-xs">Skill</TableHead><TableHead className="text-xs">Current</TableHead><TableHead className="text-xs">Required</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {empRep.skill_levels.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-4 text-sm">No skills recorded</TableCell></TableRow> :
                          empRep.skill_levels.map((sl, i) => { const gap = sl.required_level && (['S1', 'S2', 'S3', 'S4'].indexOf(sl.level) < ['S1', 'S2', 'S3', 'S4'].indexOf(sl.required_level)); return (
                            <TableRow key={i}><TableCell className="text-xs text-slate-500">{sl.skill_group}</TableCell><TableCell className="text-sm">{sl.skill_name}</TableCell><TableCell className={`text-xs font-mono ${gap ? 'text-red-600 font-bold' : ''}`}>{sl.level}</TableCell><TableCell className="text-xs font-mono">{sl.required_level || '—'}</TableCell></TableRow>
                          ); })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold mb-1">Skill Gaps ({empRep.gaps.length})</p>
                  {empRep.gaps.length === 0 ? <p className="text-sm text-emerald-600">No gaps — all required skills met.</p> : (
                    <div className="flex flex-wrap gap-1.5">
                      {empRep.gaps.map((g, i) => <Badge key={i} variant="outline" className="text-[11px] bg-red-50 text-red-700 border-red-200">{g.skill_name}: {g.current_level} → {g.required_level}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Position Eligibility */}
        <TabsContent value="pe">
          <Card className="border border-slate-200 shadow-none"><CardContent className="p-4 space-y-3">
            <p className="text-sm text-slate-600">For a functional position, see which employees meet the qualification + experience eligibility (any alternative), using the qualification hierarchy.</p>
            <div className="w-96 max-w-full"><Label className="text-xs">Functional Position</Label>
              <Select value={peFp || undefined} onValueChange={runPe}>
                <SelectTrigger data-testid="hr-pe-select"><SelectValue placeholder="Select functional position" /></SelectTrigger>
                <SelectContent>{fpList.map(fp => <SelectItem key={fp.id} value={fp.id}>{fp.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {pe && (
              <div className="space-y-3" data-testid="hr-pe-results">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-500">Requirement:</span>
                  {pe.functional_position.alternatives.length === 0 ? <Badge variant="outline" className="text-[10px]">No requirement</Badge> :
                    pe.functional_position.alternatives.map((a, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="text-[10px] text-slate-400 font-semibold">OR</span>}
                        <Badge variant="outline" className="text-[10px] bg-slate-50">{a.qualification_name}{a.min_experience_years ? ` + ${a.min_experience_years}yr` : ''}</Badge>
                      </React.Fragment>
                    ))}
                  <span className="ml-auto font-medium text-emerald-700">{pe.eligible_count}/{pe.total} eligible</span>
                </div>
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50/80">
                      <TableHead className="text-xs">Employee</TableHead><TableHead className="text-xs">Qualification</TableHead>
                      <TableHead className="text-xs">Exp (yr)</TableHead><TableHead className="text-xs">Eligible</TableHead><TableHead className="text-xs">Assigned</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {pe.employees.map(r => (
                        <TableRow key={r.employee_id} className={r.eligible ? '' : 'bg-red-50/30'}>
                          <TableCell className="text-sm">{r.name}</TableCell>
                          <TableCell className="text-xs">{r.qualification}</TableCell>
                          <TableCell className="text-xs font-mono">{r.experience_years}</TableCell>
                          <TableCell><Badge variant="outline" className={`text-[10px] ${r.eligible ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{r.eligible ? 'Yes' : 'No'}</Badge></TableCell>
                          <TableCell className="text-xs">{r.assigned ? '✓' : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Critical Skills & Backup Plans */}
        <TabsContent value="crit">
          <CriticalSkillsTab canManage={canManage} employees={emps} onSaved={loadMatrix} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!cellEdit} onOpenChange={(o) => !o && setCellEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="hr-cell-edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-pink-600" /> Update Skill Level</DialogTitle></DialogHeader>
          {cellEdit && (
            <div className="space-y-3">
              <p className="text-sm"><b>{cellEdit.empName}</b> · {cellEdit.skillName}</p>
              <p className="text-xs text-slate-500">Current: <b>{cellEdit.current || 'None'}</b>{cellEdit.required ? ` · Required: ${cellEdit.required}` : ''}</p>
              <div className="grid grid-cols-4 gap-2">
                {['S1', 'S2', 'S3', 'S4'].map(l => (
                  <Button key={l} variant={cellEdit.current === l ? 'default' : 'outline'} disabled={savingCell}
                    className={cellEdit.current === l ? 'bg-pink-600 hover:bg-pink-700 text-white' : ''}
                    onClick={() => saveCell(l)} data-testid={`hr-cell-level-${l}`}>{l}</Button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setCellEdit(null)}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
